import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import Sidebar from "./Sidebar";
import SearchBar from "./SearchBar";
import ItemList from "./ItemList";
import BillingDetails from "./BillingDetails";
import { ItemsContext } from "../context/ItemsContext";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { UtensilsCrossed, Printer, Moon, Sun, Keyboard, X } from "lucide-react";
import { toast } from "react-toastify";
import { useGstDiscount } from "../context/GstDiscountContext";
import BillModal from "./BillModal";
import { useBill } from "../context/BillContext";
import TableTransferModal from "./TableTransferModal";
import QuickStatsStrip from "./QuickStatsStrip";
import { useAuth } from "./authentication/AuthContext";

const normalizeCache = new Map();
const normalize = (str) => {
  if (!str) return "";
  if (normalizeCache.has(str)) return normalizeCache.get(str);
  const result = String(str)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[^\w\s]/g, "")
    .trim();
  normalizeCache.set(str, result);
  return result;
};

function HomePage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const section = queryParams.get("section");
  const index = queryParams.get("index");
  const orderId = queryParams.get("orderId");
  const { setBillData } = useBill();
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [orderItems, setOrderItems] = useState([]);
  const [customizerProduct, setCustomizerProduct] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [isLargeBtn, setIsLargeBtn] = useState(() => {
    return localStorage.getItem("isLargeBtn") === "true";
  });
  const [isListView, setIsListView] = useState(() => {
    return localStorage.getItem("isListView") === "true";
  });
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("posDarkMode") === "true");
  const [showCheatsheet, setShowCheatsheet] = useState(false);

  useEffect(() => {
    localStorage.setItem("isLargeBtn", isLargeBtn);
  }, [isLargeBtn]);

  useEffect(() => {
    localStorage.setItem("isListView", isListView);
  }, [isListView]);

  useEffect(() => {
    localStorage.setItem("posDarkMode", darkMode);
  }, [darkMode]);

  const [navZone, setNavZone] = useState("sidebar"); // "sidebar" or "items"
  const [navCategoryIndex, setNavCategoryIndex] = useState(0);
  const [navItemIndex, setNavItemIndex] = useState(0);

  const [billingDetails, setBillingDetails] = useState({
    name: "",
    mobile: "",
  });

  const navigate = useNavigate();

  const { items } = useContext(ItemsContext);
  const { discount, gst, setDiscount, discountType, fixedDiscountAmount } = useGstDiscount();
  const [showTableTransfer, setShowTableTransfer] = useState(false);
  const [isMobileBillOpen, setIsMobileBillOpen] = useState(false);

  // Happy hour → auto-apply discount to the current bill (badge lives in QuickStatsStrip)
  const [activeHappyHour, setActiveHappyHour] = useState(null);
  const pendingParcelRef = useRef(null);
  const { isStaff, staffRole } = useAuth();
  const canCancelOrders = !isStaff || ["cashier", "manager"].includes(staffRole);
  // Id of the happy hour whose discount we applied; the discount only changes when a
  // happy hour starts or ends, so a discount the cashier typed in isn't overwritten every poll
  const happyHourSetRef = useRef(null);
  useEffect(() => {
    const checkHappyHour = () => {
      api.get("/happy-hours/active")
        .then(res => {
          const hh = res.data.data;
          setActiveHappyHour(hh || null);
          if (hh && happyHourSetRef.current !== hh._id) {
            happyHourSetRef.current = hh._id;
            setDiscount(hh.discountPercentage);
          } else if (!hh && happyHourSetRef.current) {
            happyHourSetRef.current = null;
            setDiscount(0);
          }
        })
        .catch(() => {});
    };
    checkHappyHour();
    const timer = setInterval(checkHappyHour, 60000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (orderId && orderId !== "null") {
      fetchOrderDetails(orderId);
    } else {
      setOrderItems([]);
    }

    const handleSyncComplete = (e) => {
      // An order created offline now exists on the server — switch to its real id
      const serverId = orderId && e.detail?.idMap?.[orderId];
      if (serverId) {
        navigate(`/home?orderId=${serverId}`, { replace: true });
        return;
      }
      if (orderId && orderId !== "null") {
        fetchOrderDetails(orderId);
      }
    };

    window.addEventListener("sync-complete", handleSyncComplete);
    return () => {
      window.removeEventListener("sync-complete", handleSyncComplete);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    const hasItems = Array.isArray(items)
      ? items.length > 0
      : items?.data?.length > 0;

    if (hasItems) {
      setLoading(false);
    } else if (items && (items.length === 0 || items?.data?.length === 0)) {
      setLoading(false);
    }
  }, [items]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Let the customizer modal handle its own keyboard events
      if (customizerProduct) return;

      // Close cheatsheet on Escape
      if (showCheatsheet) {
        if (e.key === "Escape") setShowCheatsheet(false);
        return;
      }

      // 1. If typing in input/textarea/select, only allow Escape to unfocus
      if (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA" ||
        document.activeElement.tagName === "SELECT" ||
        document.activeElement.isContentEditable
      ) {
        if (e.key === "Escape") {
          document.activeElement.blur();
        }
        return;
      }

      // 2. Global Shortcuts
      // Focus Search Bar: '/' or 'Alt + S'
      if (e.key === "/" || (e.altKey && e.key.toLowerCase() === "s")) {
        e.preventDefault();
        const searchInput = document.getElementById("pos-search-input");
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }

      // Keyboard Navigation for POS
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setNavZone("items");
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setNavZone("sidebar");
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (navZone === "sidebar") {
          setNavCategoryIndex(prev => {
            const newIndex = prev + 1;
            const nodes = document.querySelectorAll('[data-nav-category]');
            if (newIndex >= nodes.length) return prev;
            nodes[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            return newIndex;
          });
        } else if (navZone === "items") {
          setNavItemIndex(prev => {
            const newIndex = prev + 1;
            const nodes = document.querySelectorAll('[data-nav-item]');
            if (newIndex >= nodes.length) return prev;
            nodes[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            return newIndex;
          });
        }
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (navZone === "sidebar") {
          setNavCategoryIndex(prev => {
            const newIndex = prev - 1;
            if (newIndex < 0) return 0;
            const nodes = document.querySelectorAll('[data-nav-category]');
            if (nodes[newIndex]) {
              nodes[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
            return newIndex;
          });
        } else if (navZone === "items") {
          setNavItemIndex(prev => {
            const newIndex = prev - 1;
            if (newIndex < 0) return 0;
            const nodes = document.querySelectorAll('[data-nav-item]');
            if (nodes[newIndex]) {
              nodes[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
            return newIndex;
          });
        }
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (navZone === "items") {
          const nodes = document.querySelectorAll('[data-nav-item]');
          if (nodes[navItemIndex]) {
            const addBtn = nodes[navItemIndex].querySelector('[data-nav-add]');
            if (addBtn) addBtn.click();
          }
        } else if (navZone === "sidebar") {
          const nodes = document.querySelectorAll('[data-nav-category]');
          if (nodes[navCategoryIndex]) {
            nodes[navCategoryIndex].click();
          }
        }
      }

      // Generate Bill: 'Alt + G' or 'Ctrl + Enter' or 'Cmd + Enter'
      if ((e.altKey && e.key.toLowerCase() === "g") || (e.ctrlKey && e.key === "Enter") || (e.metaKey && e.key === "Enter")) {
        e.preventDefault();
        generateBillSlip(paymentMethod);
      }

      // Cancel Order: 'Alt + C'
      if (e.altKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        if (orderId && !canCancelOrders) return;
        handleCancelOrder();
      }

      // Go to Floor Plan (Select Place): 'Alt + H'
      if (e.altKey && e.key.toLowerCase() === "h") {
        e.preventDefault();
        navigate("/select-place");
      }

      // Keyboard cheatsheet: '?'
      if (e.key === "?") {
        e.preventDefault();
        setShowCheatsheet(prev => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, billingDetails, orderItems, orderId, billingLoading, gst, discount, navigate, navZone, navItemIndex, customizerProduct, showCheatsheet]);

  const fetchOrderDetails = async (orderId) => {
    try {
      const res = await api.get(`/orders/${orderId}`);
      const order = res.data;

      if (order.status === "CLOSED") {
        toast.info("This order is already closed");
        navigate("/select-place", { replace: true });
        return;
      }

      setOrderInfo(order);

      setOrderItems(
        order.items.map((i) => {
          // Reconstruct a cartKey for local de-duplication on reload
          const variantKey = i.variant?.name ? `__v:${i.variant.name}` : "";
          const addonsKey =
            i.addOns && i.addOns.length > 0
              ? `__a:${i.addOns.map((a) => a.name).join(",")}`
              : "";
          return {
            cartKey: `${i.itemId}${variantKey}${addonsKey}`,
            itemId: i.itemId,
            name: i.name,
            qty: i.qty,
            price: i.price,
            variant: i.variant?.name || null,
            addOns: i.addOns || [],
            note: i.note || "",
          };
        }),
      );
    } catch (error) {
      console.error("Failed to fetch order:", error);
    }
  };

  const updateOrderBackend = async (updatedItems) => {
    if (!orderId) return;

    try {
      // Strip frontend-only cartKey before sending to backend
      const payload = updatedItems.map(({ cartKey, variant, addOns, ...rest }) => ({
        ...rest,
        ...(variant ? { variant: { name: variant } } : {}),
        ...(addOns && addOns.length > 0 ? { addOns } : {}),
      }));
      await api.patch(`/orders/${orderId}/items`, { items: payload });
    } catch (err) {
      if (err.response?.data?.message === "Order is closed") {
        return;
      }
      console.error("Order update failed", err);
    }
  };

  // Expand a combo into its items with prices scaled so the lines sum to the combo price
  const addComboToOrder = (combo) => {
    const itemsTotal = combo.items.reduce((s, it) => s + it.price * it.qty, 0);
    if (itemsTotal <= 0) return;
    const factor = combo.comboPrice / itemsTotal;
    let allocated = 0;
    const stamp = Date.now();

    const comboItems = combo.items.map((it, idx) => {
      let unit;
      if (idx === combo.items.length - 1) {
        // Last line absorbs rounding so the total is exactly the combo price
        unit = Math.round(((combo.comboPrice - allocated) / it.qty) * 100) / 100;
      } else {
        unit = Math.round(it.price * factor * 100) / 100;
        allocated += unit * it.qty;
      }
      return {
        cartKey: `combo:${combo._id}:${it.productId}:${stamp}`,
        itemId: it.productId,
        name: `${it.name} [${combo.name}]`,
        qty: it.qty,
        price: unit,
        variant: null,
        addOns: [],
        note: "",
      };
    });

    const updatedItems = [...orderItems, ...comboItems];
    setOrderItems(updatedItems);
    updateOrderBackend(updatedItems);
  };

  const removeFromOrder = (itemToRemove) => {
    const key = itemToRemove.cartKey || itemToRemove.itemId;
    const updatedItems = orderItems.filter(
      (item) => (item.cartKey || item.itemId) !== key,
    );

    setOrderItems(updatedItems);
    updateOrderBackend(updatedItems);
  };

  const allItems = useMemo(() => Array.isArray(items) ? items : items?.data || [], [items]);

  const filteredItems = useMemo(() => {
    const search = normalize(searchQuery);
    if (!search) return allItems;
    return allItems.filter((item) => {
      const productName = normalize(item?.productName || item?.name);
      return productName.includes(search);
    });
  }, [allItems, searchQuery]);

  const addToOrder = (item, selectedVariant = null, selectedAddOns = []) => {
    // Build a unique frontend cart key for de-duplication
    const variantKey = selectedVariant ? `__v:${selectedVariant.name}` : "";
    const addonsKey =
      selectedAddOns.length > 0
        ? `__a:${selectedAddOns.map((a) => a.name).join(",")}`
        : "";
    const cartKey = `${item._id}${variantKey}${addonsKey}`;

    // Compute effective price: variant price (if chosen) + sum of add-on prices
    const basePrice = selectedVariant
      ? Number(selectedVariant.price)
      : Number(item.price);
    const addOnsTotal = selectedAddOns.reduce(
      (sum, a) => sum + Number(a.price),
      0,
    );
    const effectivePrice = basePrice + addOnsTotal;

    const existingItem = orderItems.find((oi) => (oi.cartKey || oi.itemId) === cartKey);

    let updatedItems;
    if (existingItem) {
      updatedItems = orderItems.map((oi) =>
        (oi.cartKey || oi.itemId) === cartKey ? { ...oi, qty: oi.qty + 1 } : oi,
      );
    } else {
      updatedItems = [
        ...orderItems,
        {
          cartKey,
          itemId: item._id,
          name: item.productName,
          qty: 1,
          price: effectivePrice,
          variant: selectedVariant ? selectedVariant.name : null,
          addOns: selectedAddOns.map((a) => ({ name: a.name, price: Number(a.price) })),
          note: "",
        },
      ];
    }

    setOrderItems(updatedItems);
    updateOrderBackend(updatedItems);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredItems.length > 0) {
        const firstItem = filteredItems[0];
        const hasVariants = firstItem.variants && firstItem.variants.length > 0;
        const hasAddOns = firstItem.addOns && firstItem.addOns.length > 0;

        if (hasVariants || hasAddOns) {
          setCustomizerProduct(firstItem);
        } else {
          addToOrder(firstItem, null, []);
        }
        setSearchQuery("");
      }
    }
  };


  const addToOrderincrease = (item) => {
    const key = item.cartKey || item.itemId;
    console.log("DEC", item.qty, key); const updatedItems = orderItems.map((orderItem) =>
      (orderItem.cartKey || orderItem.itemId) === key
        ? { ...orderItem, qty: orderItem.qty + 1 }
        : orderItem,
    );

    setOrderItems(updatedItems);
    updateOrderBackend(updatedItems);
  };

  const addToOrderdecrease = (item) => {
    const key = item.cartKey || item.itemId;
    const updatedItems = orderItems
      .map((orderItem) =>
        (orderItem.cartKey || orderItem.itemId) === key
          ? { ...orderItem, qty: orderItem.qty - 1 }
          : orderItem,
      )
      .filter((orderItem) => orderItem.qty > 0);

    setOrderItems(updatedItems);
    updateOrderBackend(updatedItems);
  };

  const updateItemQty = (itemId, newQty) => {
    const updatedItems = orderItems.map((orderItem) =>
      (orderItem.cartKey || orderItem.itemId) === itemId
        ? { ...orderItem, qty: newQty }
        : orderItem,
    );

    setOrderItems(updatedItems);
    updateOrderBackend(updatedItems);
  };

  const updateItemNote = (cartKeyOrItemId, note) => {
    setOrderItems(prev =>
      prev.map(item =>
        (item.cartKey || item.itemId) === cartKeyOrItemId ? { ...item, note } : item
      )
    );
  };

  const calculateTotal = () => {
    return orderItems.reduce((total, item) => total + item.price * item.qty, 0);
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails({ ...billingDetails, [name]: value });
  };

  const generateBillSlip = async (paymentMethod = "COD", options = {}) => {
    console.log("[HomePage] generateBillSlip called. paymentMethod:", paymentMethod, "options:", options, "billingDetails:", billingDetails);
    
    if (!orderItems || orderItems.length === 0) {
      console.warn("[HomePage] generateBillSlip rejected: orderItems is empty");
      toast.error("Add at least one item");
      return;
    }

    // Remove the requirement for orderId to exist beforehand
    // We will initialize it dynamically if it's missing!

    const mobileStr = String(billingDetails.mobile || "").trim();
    if (mobileStr && mobileStr.length !== 10) {
      console.warn("[HomePage] generateBillSlip rejected: invalid mobile number:", mobileStr);
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    const nameStr = String(billingDetails.name || "").trim() || "Walk-in Customer";

    if (billingLoading) {
      console.warn("[HomePage] generateBillSlip rejected: already loading");
      return;
    }

    try {
      setBillingLoading(true);

      // Reuse the takeaway order from a failed attempt so retries don't leave orphan parcels
      let currentOrderId = orderId || pendingParcelRef.current;

      if (!currentOrderId) {
        // Create an on-the-fly direct/takeaway order
        const orderRes = await api.post("/orders/parcel");
        currentOrderId = orderRes.data._id;
        pendingParcelRef.current = currentOrderId;
      }

      // Force sync the latest React cart to the database before generating the bill
      // This prevents "Order has no items" errors caused by race conditions or dropped network requests
      const payload = orderItems.map(({ cartKey, variant, addOns, ...rest }) => ({
        ...rest,
        ...(variant ? { variant: { name: variant } } : {}),
        ...(addOns && addOns.length > 0 ? { addOns } : {}),
      }));
      console.log("[HomePage] Syncing order items before bill:", payload);
      const patchRes = await api.patch(`/orders/${currentOrderId}/items`, { items: payload });
      console.log("[HomePage] Sync successful. DB Order items:", patchRes.data.items);

      const billRes = await api.post(`/bill/from-order/${currentOrderId}`, {
        name: nameStr,
        mobile: mobileStr || undefined,
        paymentMethod,
        gst,
        discount: discountType === "fixed" ? 0 : discount,
        flatDiscount: discountType === "fixed" ? fixedDiscountAmount : 0,
        redeemPoints: options.redeemPoints || false,
        useWallet: options.useWallet || false,
        splitPayments: options.splitPayments || [],
      });

      setBillData(billRes.data);
      pendingParcelRef.current = null;
      setOrderItems([]);
      setIsBillModalOpen(true);
      setTimeout(() => {
        setIsMobileBillOpen(false);
      }, 150);
    } catch (err) {
      console.error("[HomePage] Billing error:", err.response?.data || err);
      const message = err.response?.data?.message || err.response?.data?.error || err.message || "Billing failed";
      toast.error(message);
    } finally {
      setBillingLoading(false);
    }
  };

  const [kotConfirm, setKotConfirm] = useState(null);

  const doPrintKOT = (order, itemsToPrint, kotRound, isReprint = false) => {
    const isAddOn = kotRound > 1 && !isReprint;
    const html = `
      <html><head><title>KOT #${order.orderNumber}</title>
      <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;font-size:13px;padding:8px 10px}
      .header{text-align:center;border-bottom:2px dashed #000;padding-bottom:8px;margin-bottom:8px}
      .kot-title{font-size:17px;font-weight:900;text-transform:uppercase;letter-spacing:1px}
      .addon-banner{background:#000;color:#fff;text-align:center;font-weight:900;font-size:13px;padding:4px 6px;margin:6px 0;letter-spacing:1px}
      .meta{font-size:12px;line-height:1.6;border-bottom:1px dashed #000;padding-bottom:6px;margin-bottom:6px}
      .meta-row{display:flex;justify-content:space-between}
      table{width:100%;border-collapse:collapse}
      th{font-weight:900;font-size:12px;padding:3px 0;border-bottom:1px solid #000;text-align:left}
      th.qty,td.qty{text-align:right;width:30px}
      td{padding:5px 0 3px;font-weight:700;vertical-align:top}
      td.qty{font-weight:900;font-size:16px}
      .sub{font-size:11px;color:#333;font-style:italic;font-weight:500}
      .item-note{font-size:10px;color:#666;font-style:italic;background:#f8f8f8;border-left:2px solid #bbb;padding:1px 4px;margin-top:2px;border-radius:2px}
      .footer{text-align:center;border-top:1px dashed #000;margin-top:12px;padding-top:6px;font-size:10px}
      .footer-space{height:40px}@page{margin:2mm 0}</style></head>
      <body>
      <div class="header"><div class="kot-title">Kitchen Order Ticket</div>
      <div style="font-size:11px;font-weight:700;margin-top:3px">${isReprint ? '🔁 REPRINT' : `Round ${kotRound}`} | Status: ${order.status || 'NEW'}</div></div>
      ${isAddOn ? '<div class="addon-banner">⚡ ADD-ON ORDER ⚡</div>' : ''}
      <div class="meta">
        <div class="meta-row"><span><strong>KOT No:</strong> #${order.orderNumber}</span><span><strong>Type:</strong> ${order.orderType === 'DINE_IN' ? 'Dine-In' : 'Takeaway'}</span></div>
        ${order.orderType === 'DINE_IN' ? `<div class="meta-row" style="margin-top:2px"><span><strong>Table:</strong> ${order.unitNumber}</span><span><strong>Floor:</strong> ${order.floorName} / ${order.sectionName}</span></div>` : `<div class="meta-row" style="margin-top:2px"><span><strong>Token:</strong> ${order.parcelToken || 'P' + order.orderNumber}</span></div>`}
        <div style="margin-top:2px"><strong>Time:</strong> ${new Date().toLocaleString('en-IN')}</div>
      </div>
      <table><thead><tr><th>Item</th><th class="qty">Qty</th></tr></thead><tbody>
      ${itemsToPrint.map(item => `<tr><td><div>${item.name}</div>${item.variant ? `<div class="sub">↳ ${item.variant?.name || item.variant}</div>` : ''}${item.addOns && item.addOns.length > 0 ? `<div class="sub">+ ${item.addOns.map(a => a.name).join(', ')}</div>` : ''}${item.note ? `<div class="item-note">📝 ${item.note}</div>` : ''}</td><td class="qty">${item.qty}</td></tr>`).join('')}
      </tbody></table>
      ${order.notes ? `<div style="margin-top:8px;border-top:1px dashed #000;padding-top:5px;font-size:12px"><strong>Note:</strong> ${order.notes}</div>` : ''}
      <div class="footer">CloudRasoi Billing System</div><div class="footer-space"></div>
      <script>window.onload = function(){ window.print(); window.onafterprint = function(){ window.close(); }; };</script>
      </body></html>
    `;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");
    if (!printWindow) {
      toast.warn("Please allow popups to print KOT");
      URL.revokeObjectURL(url);
      return;
    }
    printWindow.addEventListener("load", () => URL.revokeObjectURL(url), { once: true });
  };

  const handlePrintKOT = async (forceReprint = false) => {
    if (!orderId) {
      toast.warn("Save the order first before printing KOT");
      return;
    }
    if (!orderItems || orderItems.length === 0) {
      toast.warn("Add items before printing KOT");
      return;
    }
    try {
      // Sync latest items first
      const payload = orderItems.map(({ cartKey, variant, addOns, ...rest }) => ({
        ...rest,
        ...(variant ? { variant: { name: variant } } : {}),
        ...(addOns && addOns.length > 0 ? { addOns } : {}),
      }));
      await api.patch(`/orders/${orderId}/items`, { items: payload });

      const res = await api.post(`/orders/${orderId}/kot-print`, { forceReprint });
      const { alreadyPrinted, kotRound, itemsToPrint, order: updatedOrder } = res.data;

      if (alreadyPrinted && !forceReprint) {
        setKotConfirm({ order: updatedOrder, kotRound });
        return;
      }
      doPrintKOT(updatedOrder, itemsToPrint, kotRound, forceReprint);
      toast.success(forceReprint ? "KOT Reprinted!" : `KOT Round ${kotRound} sent to kitchen!`);
    } catch (err) {
      console.error("KOT print failed:", err);
      toast.error(err.response?.data?.message || "KOT print failed");
    }
  };

  const handleCancelOrder = async () => {
  if (!orderId) {
    // For Direct POS with no created order, just clear the local cart
    setOrderItems([]);
    setBillingDetails({ name: "", mobile: "" });
    toast.success("Cart cleared");
    navigate("/select-place", { replace: true });
    return;
  }

  let cancelReason = "";

  toast.info(
    <div>
      <p className="font-semibold mb-2">
        Are you sure you want to cancel this order?
      </p>

      <input
        type="text"
        placeholder="Enter cancellation reason..."
        className="w-full border rounded px-2 py-1 mb-3"
        onChange={(e) => (cancelReason = e.target.value)}
      />

      <div className="flex justify-center gap-3">
        <button
          className="bg-red-600 text-white px-4 py-1 rounded"
          onClick={async () => {
            try {
              await api.post(`/orders/${orderId}/cancel`, {
                reason: cancelReason.trim(),
              });

              toast.dismiss();
              toast.success("Order cancelled successfully");

              setOrderItems([]);
              setBillingDetails({ name: "", mobile: "" });

              navigate("/select-place", { replace: true });

            } catch (err) {
              toast.dismiss();
              toast.error(
                err.response?.data?.message || "Cancel failed"
              );
            }
          }}
        >
          Yes, Cancel
        </button>

        <button
          className="bg-gray-300 px-4 py-1 rounded"
          onClick={() => toast.dismiss()}
        >
          No
        </button>
      </div>
    </div>,
    {
      position: "top-center",
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    }
  );
};



  useEffect(() => {
    if (isMobileBillOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => document.body.classList.remove("overflow-hidden");
  }, [isMobileBillOpen]);

  return (
    <div className={`flex flex-col h-screen transition-colors duration-300 ${darkMode ? "bg-gray-950" : ""}`}>
      {!isMobileBillOpen && (
        <button
          onClick={() => setIsMobileBillOpen(true)}
          className="lg:hidden fixed bottom-2 right-0 z-50 -translate-y-1/2
               bg-teal-600 text-white p-2 h-16 rounded-l-full
               shadow-lg flex items-center justify-center text-base font-bold"
        >
          <UtensilsCrossed size={18} />
          Checkout: {orderItems.length}
        </button>
      )}

      {/* KOT Already-Printed Confirmation Modal */}
      {kotConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl p-7 max-w-sm w-full mx-4 text-center">
            <div className="text-5xl mb-3">🍳</div>
            <h2 className="text-xl font-black text-slate-800 mb-1">Already Sent to Kitchen!</h2>
            <p className="text-sm text-slate-500 font-medium mb-1">
              All items already KOT printed (Round {kotConfirm.kotRound}). No new items added yet.
            </p>
            <p className="text-xs text-slate-400 font-semibold mb-5">Do you want to reprint the full KOT for the kitchen?</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { handlePrintKOT(true); setKotConfirm(null); }}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-sm rounded-2xl flex items-center justify-center gap-2 transition"
              >
                <Printer size={14} /> Yes, Reprint All Items
              </button>
              <button
                onClick={() => setKotConfirm(null)}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-sm rounded-2xl transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Table Transfer Modal ── */}
      {showTableTransfer && orderId && (
        <TableTransferModal
          orderId={orderId}
          currentTable={orderInfo}
          onClose={() => setShowTableTransfer(false)}
          onTransferred={(target) => {
            setShowTableTransfer(false);
            setOrderInfo(prev => prev ? { ...prev, floorName: target.floorName, sectionName: target.sectionName, unitNumber: target.unitNumber, roomType: target.roomType } : prev);
          }}
        />
      )}

      {/* ── Keyboard Cheatsheet Modal ── */}
      {showCheatsheet && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowCheatsheet(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-slate-100 rounded-xl"><Keyboard size={16} className="text-slate-600"/></div>
                <h2 className="text-base font-black text-slate-800">Keyboard Shortcuts</h2>
              </div>
              <button onClick={() => setShowCheatsheet(false)} className="p-1.5 hover:bg-slate-100 rounded-xl transition-colors">
                <X size={15} className="text-slate-400"/>
              </button>
            </div>
            <div className="space-y-0.5">
              {[
                { keys: ["/", "Alt+S"],       label: "Focus search" },
                { keys: ["Enter"],             label: "Add first result to cart" },
                { keys: ["←", "→"],            label: "Switch sidebar / items" },
                { keys: ["↑", "↓"],            label: "Navigate categories / items" },
                { keys: ["Alt+G", "Ctrl+↵"],   label: "Generate bill" },
                { keys: ["Alt+C"],             label: "Cancel order" },
                { keys: ["Alt+P"],             label: "Toggle sales privacy (hide/show)" },
                { keys: ["Alt+H"],             label: "Go to floor plan" },
                { keys: ["?"],                 label: "Toggle this cheatsheet" },
                { keys: ["Esc"],               label: "Close modal / unfocus" },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-sm text-slate-600 font-medium">{row.label}</span>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                    {row.keys.map((k, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && <span className="text-[10px] text-slate-300 font-medium mx-0.5">or</span>}
                        <kbd className="px-2 py-1 bg-slate-100 border border-slate-200/80 text-slate-700 text-[11px] font-mono font-bold rounded-lg shadow-sm whitespace-nowrap">{k}</kbd>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Today's quick-stats strip (shared) ── */}
      <QuickStatsStrip
        onAddCombo={addComboToOrder}
        hasActiveOrder={Boolean(orderId)}
        onOrderMerged={(order) => {
          // If the POS is currently on the merged-into order, refresh its items
          if (order?._id === orderId) {
            fetchOrderDetails(orderId);
          }
        }}
        extraControls={
          <>
            <button
              onClick={() => setShowCheatsheet(true)}
              title="Keyboard shortcuts (?)"
              className="w-8 h-8 rounded-xl hover:bg-white/10 text-slate-500 hover:text-slate-200 transition-colors flex items-center justify-center"
            >
              <Keyboard size={14}/>
            </button>
            <button
              onClick={() => setDarkMode(d => !d)}
              title="Toggle dark mode"
              className={`w-8 h-8 rounded-xl transition-colors flex items-center justify-center ${darkMode ? "bg-yellow-500/15 hover:bg-yellow-500/25" : "hover:bg-white/10"}`}
            >
              {darkMode
                ? <Sun size={14} className="text-yellow-400"/>
                : <Moon size={14} className="text-slate-500 hover:text-slate-200"/>
              }
            </button>
          </>
        }
      />

      <main className={`flex flex-grow p-4 min-h-0 transition-colors duration-300 ${darkMode ? "bg-gray-950" : "bg-slate-50"}`}>
        <div className="flex flex-col md:flex-row w-full h-full">
          <Sidebar
            setSelectedCategory={setSelectedCategory}
            setSearchQuery={setSearchQuery}
            navZone={navZone}
            navCategoryIndex={navCategoryIndex}
            setNavCategoryIndex={setNavCategoryIndex}
            setNavZone={setNavZone}
          />

          <div className="flex flex-col flex-1 min-w-0">
             <SearchBar
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              items={items}
              orderInfo={orderInfo}
              onSearchKeyDown={handleSearchKeyDown}
            />

            {loading ? (
              <div className="flex flex-col items-center justify-center flex-grow bg-white m-2 rounded-lg shadow-inner">
                <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-500 font-medium">
                  Fetching products...
                </p>
              </div>
            ) : (
              <div className="flex-grow overflow-y-auto min-h-0 pb-16 scrollbar-thin scrollbar-thumb-slate-200">
                <div className="flex justify-end gap-2 px-2 pb-2">
                  <button 
                    onClick={() => setIsListView(!isListView)}
                    className="flex items-center rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 transition-colors px-3 py-1.5 gap-1.5"
                  >
                    <span className="text-sm shrink-0">{isListView ? "🔲" : "📄"}</span>
                    <span className="text-xs font-semibold whitespace-nowrap">
                      {isListView ? "Grid View" : "List View"}
                    </span>
                  </button>
                  <button 
                    onClick={() => setIsLargeBtn(!isLargeBtn)}
                    className="flex items-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors px-3 py-1.5 gap-1.5"
                  >
                    <span className="text-sm shrink-0">{isLargeBtn ? "➖" : "➕"}</span>
                    <span className="text-xs font-semibold whitespace-nowrap">
                      {isLargeBtn ? "Normal Add" : "Large Add"}
                    </span>
                  </button>
                </div>
                <ItemList
                  filteredItems={filteredItems}
                  addToOrder={addToOrder}
                  customizerProduct={customizerProduct}
                  setCustomizerProduct={setCustomizerProduct}
                  isLargeBtn={isLargeBtn}
                  isListView={isListView}
                  navZone={navZone}
                  navItemIndex={navItemIndex}
                  setNavItemIndex={setNavItemIndex}
                  setNavZone={setNavZone}
                />
              </div>
            )}
          </div>
          <div
            className={`
    ${isMobileBillOpen ? "translate-x-0" : "translate-x-full"}
    lg:translate-x-0
    fixed lg:sticky
    top-[56px] lg:top-4 right-0
    h-[calc(100%-56px)] lg:h-auto lg:max-h-[calc(100vh-32px)]
    lg:self-start
    w-[90%] lg:w-auto lg:basis-[30%]
    lg:min-w-[260px] lg:max-w-[400px]
    shrink-0
    ${darkMode ? "bg-gray-800" : "bg-white"}
    lg:ml-4
    shadow-xl lg:shadow-lg
    transform-gpu transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
    z-50
    overflow-y-auto
  `}
          >
            <BillingDetails
              billingLoading={billingLoading}
              section={section}
              index={index}
              billingDetails={billingDetails}
              handleBillingChange={handleBillingChange}
              orderItems={orderItems}
              calculateTotal={calculateTotal}
              generateBillSlip={(method, opts) => {
                generateBillSlip(method, opts);
              }}
              removeFromOrder={removeFromOrder}
              orderId={orderId}
              addToOrderincrease={addToOrderincrease}
              addToOrderdecrease={addToOrderdecrease}
              updateItemQty={updateItemQty}
              updateItemNote={updateItemNote}
              darkMode={darkMode}
              happyHour={activeHappyHour}
              onTransferTable={orderId ? () => setShowTableTransfer(true) : null}
              orderInfo={orderInfo}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              onCancelOrder={!orderId || canCancelOrders ? handleCancelOrder : undefined}
              onPrintKOT={orderId ? () => handlePrintKOT(false) : null}
            />
          </div>
        </div>
      </main>

      {isBillModalOpen && (
        <BillModal
          closeModal={() => {
            setIsBillModalOpen(false);
            setOrderItems([]);
            setBillingDetails({ name: "", mobile: "" });
            navigate("/select-place", { replace: true });
          }}
        />
      )}
    </div>
  );
}

export default HomePage;
